import { CommonModule } from '@angular/common';
import { HttpClientModule, HttpClientXsrfModule } from '@angular/common/http';
import { NgModule, Optional, SkipSelf } from '@angular/core';
import { AlertComponent } from '@app/core/components/alert/alert.component';
import { AlertMessageComponent } from '@app/core/components/alert/alert-message.component';
import { GlobalLoaderComponent } from '@app/core/components/loader/global-loader.component';
import { ModalModule } from '@app/core/components/modal/modal.module';
import { BootstrapService } from '@app/core/config/bootstrap.service';
import { SelectivePreloadingStrategy } from '@app/core/config/router/selective-preload-strategy';
import {
  authenticationInterceptor,
  errorInterceptor,
  loaderInterceptor,
  tokenInterceptor
} from '@app/core/interceptors/providers';
import { AppConfigService } from '@app/core/services/app-config.service';
import { CSRF_COOKIE_NAME, CSRF_HEADER_NAME } from '@app/core/services/auth.constants';
import { AuthService } from '@app/core/services/auth.service';
import { DeviceService } from '@app/core/services/device.service';
import { DictionaryService } from '@app/core/services/dictionary.service';
import { GraphqlService } from '@app/core/services/graphql.service';
import { LocalStorageService } from '@app/core/services/local-storage.service';
import { RedirectService } from '@app/core/services/redirect.service';
import { UserService } from '@app/core/services/user.service';
import { UserDataService } from '@app/core/services/user-data.service';
import { WebSocketService } from '@app/core/services/web-socket.service';
import { WindowService } from '@app/core/services/window.service';
import { PcsTranslateService } from '@app/core/translate/translate.service';
import { PcsTranslateModule } from '@app/core/translate/translate-module';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { RxStompService } from '@stomp/ng2-stompjs';
import { ApolloModule } from 'apollo-angular';
import { GridTranslateService, NgRxDataGridModule } from 'ngrx-data-grid';
import { CookieService } from 'ngx-cookie-service';
import { DragAndDropModule, PcsComponentLibraryModule, storageServiceProvider } from 'pcs-component-library';

import { throwIfAlreadyLoaded } from './module-import-guard';

const components = [
  AlertComponent,
  AlertMessageComponent,
  GlobalLoaderComponent,
];

const services = [
  RxStompService,
  WindowService,
  AppConfigService,
  BootstrapService,
  LocalStorageService,
  AuthService,
  RedirectService,
  UserService,
  DictionaryService,
  CookieService,
  WebSocketService,
  UserDataService,
  GraphqlService,
  DeviceService
];

const interceptors = [
  tokenInterceptor,
  authenticationInterceptor,
  errorInterceptor,
  loaderInterceptor
];

@NgModule({
  imports: [
    CommonModule,
    HttpClientModule, // provides HttpClient for HttpLink for Apollo
    ApolloModule,

    HttpClientXsrfModule.withOptions({
      cookieName: CSRF_COOKIE_NAME,
      headerName: CSRF_HEADER_NAME,
    }),

    PcsTranslateModule,
    NgbModule,
    DragAndDropModule.forRoot(),
    TranslateModule,
    ModalModule,
    NgRxDataGridModule.forRoot({stateKey: 'grid'}),
    PcsComponentLibraryModule.forRoot()
  ],
  declarations: [
    ...components
  ],
  providers: [
    ...services,
    SelectivePreloadingStrategy,
    ...interceptors,
    {
      provide: GridTranslateService,
      useExisting: PcsTranslateService
    },
    // TODO AIN this will be removed once we store features in DB
    storageServiceProvider(LocalStorageService)
  ],
  exports: [
    ...components
  ]
})
export class CoreModule {
  constructor(@Optional() @SkipSelf() private parentModule: CoreModule) {
    if (this.parentModule) {
      throwIfAlreadyLoaded(this.parentModule, CoreModule.name);
    }
  }
}
